import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import { usePost } from "../../../../hooks/usePost";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setRefresh } from "../../../../redux/refreshSlice";
import { useUpdate } from "../../../../hooks/useUpdate";

const ProductItemForm = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      medicineItemName: "",
      code: "",
      barcode: "",
      description: "",
    },
  });

  const { id } = useParams();
  const { editProductItem } = useSelector((state) => state.productItem);
  const { postData, loading } = usePost(`/medicine-item?relatedProduct=${id}`);
  const { updateData, loading: updateLoading } = useUpdate(`/medicine-item`);
  const { refresh } = useSelector((state) => state.refresh);
  const dispatch = useDispatch();

  const onSubmit = async (data) => {
    if (editProductItem.isEditMode) {
      await updateData(editProductItem?.data?._id, data);
    } else {
      await postData(data);
    }
    reset();
    dispatch(setRefresh(!refresh));
  };

  useEffect(() => {
    if (!editProductItem.isEditMode)
      reset({
        medicineItemName: "",
        code: "",
        barcode: "",
        description: "",
      });

    if (editProductItem.isEditMode && editProductItem.data) {
      reset({
        medicineItemName: editProductItem?.data?.medicineItemName,
        code: editProductItem?.data?.code,
        barcode: editProductItem?.data?.barcode,
        description: editProductItem?.data?.description,
      });
    } else {
      reset({
        medicineItemName: "",
        code: "",
        barcode: "",
        description: "",
      });
    }
  }, [editProductItem]);

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {editProductItem?.isEditMode ? "Edit" : "Create"} Product Item
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="medicineItemName"
              control={control}
              rules={{
                required: "Name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Name"
                  variant="outlined"
                  error={!!errors.medicineItemName}
                  helperText={errors.medicineItemName?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: "Code is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Code"
                  variant="outlined"
                  error={!!errors.code}
                  helperText={errors.code?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="barcode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Barcode"
                  variant="outlined"
                  error={!!errors.barcode}
                  helperText={errors.barcode?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Description"
                  variant="outlined"
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => reset()}
                sx={{ minWidth: 100 }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ minWidth: 100 }}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {loading
                  ? editProductItem?.isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : editProductItem?.isEditMode
                    ? "Update"
                    : "Save"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ProductItemForm;
